import React, { useCallback, useMemo, useEffect } from 'react';
import Logo from 'components/common/logo';
import { Link, useHistory } from 'react-router-dom';
import logoRobot from 'style/imgs/logo_robot.svg';
import firstStep from 'style/imgs/first_step.svg';
import secondStep from 'style/imgs/second_step.svg';
import thirdStep from 'style/imgs/third_step.svg';
import fourthStep from 'style/imgs/fourth_step.svg';
import { IRootState } from 'store';
import { UnitSettings } from 'types';
import { connect, useDispatch } from 'react-redux';
import Lock from 'style/imgs/icons/locked.svg';
import buttons from 'style/buttons.module.scss';
import { hideLiteracyModal, showLiteracyModal } from 'actions/app-state';
import MobileService from 'services/mobile';
import styles from './index.module.scss';

const mapStateToProps = (state: IRootState) => ({
  units: state.scholars.units,
  user: state.users.userData,
  literaclyModalResult: state.appState.literacyModalResult,
  rootLink: state.auth.rootLink,
});

type Props = ReturnType<typeof mapStateToProps>;

function getTableLink(settings?: UnitSettings) {
  const base = '/onboarding/timetable';

  if (!settings) {
    return base;
  }

  if (settings.permissionToEditTimetable) {
    return '/onboarding/timetable';
  }

  if (settings.permissionToEditResources) {
    return '/onboarding/timetable/resources';
  }

  return base;
}

let literacyModalIssuer: string;

const StepsAdminCorner: React.FC<Props> = ({
  units,
  user,
  literaclyModalResult,
  rootLink,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const activeUnit = units?.find((u) => u.id === user?.activeScholarId);
  const settings = activeUnit?.settings;

  const linksDisabled = !units || units.length === 0;
  const tableUrl = getTableLink(settings);

  useEffect(() => {
    if (rootLink === '/registration-success') {
      history.push(rootLink);
    }
  }, [rootLink, history]);

  const openLiteracyModal = useCallback(
    (url: string) => {
      literacyModalIssuer = url;
      dispatch(showLiteracyModal());
    },
    [dispatch]
  );

  useMemo(() => {
    if (literaclyModalResult) {
      dispatch(hideLiteracyModal());
      const state = literacyModalIssuer === '/onboarding/profile' && {
        mode: 'create',
      };
      history.push(literacyModalIssuer, state);
    }
  }, [literaclyModalResult, history, dispatch]);

  return (
    <div className={styles.root}>
      <div className={styles.logo}>
        <img src={logoRobot} alt="logo_robot" />
        <Logo />
      </div>
      <h1 className="deep-gray-heading">What do you want to do?</h1>
      {user?.type === 'parent' ? (
        <div className={styles.selectBlock}>
          <button
            type="button"
            onClick={() => openLiteracyModal('/onboarding/profile')}
            className={`${styles.selectBlockItemBottom} ${styles.trapezoid} ${
              MobileService.isMobile ? styles.tablet : ''
            }`}
          >
            <div>
              <div>
                <img src={firstStep} alt="teacher" />
              </div>
              <span className="message-title">Create Scholar Profile</span>
              <p className="message-text">
                Create a profile for your scholar and set permissions to
                determine their ability to edit their learning workflow and
                their digital resources
              </p>
            </div>
            <img className={styles.lockBtm} src={Lock} alt="" />
          </button>
          <Link
            to="/onboarding/choose-map/"
            className={`${styles.selectBlockItemTop} ${styles.trapezoid} ${
              linksDisabled && styles.linkDisabled
            } ${MobileService.isMobile ? styles.tablet : ''}`}
          >
            <div>
              <div>
                <img src={secondStep} alt="parent" />
              </div>
              <span className="message-title">Choose Map Template</span>
              <p className="message-text">
                Select a template from the library that will delight your
                scholar.
              </p>
            </div>
            {/* <img className={styles.lockTp} src={Lock} alt="" /> */}
          </Link>
          <button
            type="button"
            onClick={() => {
              if (
                !settings?.permissionToEditResources &&
                !settings?.permissionToEditTimetable
              ) {
                openLiteracyModal(tableUrl);
              } else {
                history.push(tableUrl);
              }
            }}
            className={`${styles.selectBlockItemBottom} ${styles.trapezoid} ${
              (linksDisabled || !tableUrl) && styles.linkDisabled
            }`}
          >
            <div>
              <div>
                <img src={thirdStep} alt="teacher" />
              </div>
              <span className="message-title">
                {linksDisabled
                  ? `Add timetable and Web Addresses`
                  : `Timetable and Web Addresses`}
              </span>
              <p className="message-text">
                Add a schedule, and the web addresses to any digital resources
                your scholar uses.
              </p>
            </div>
            {!settings?.permissionToEditResources &&
              !settings?.permissionToEditTimetable && (
                <img className={styles.lockTp} src={Lock} alt="" />
              )}
          </button>
          <button
            type="button"
            className={`${styles.selectBlockItemTop} ${styles.trapezoid}`}
            onClick={() => openLiteracyModal('/dashboard/scholars')}
          >
            <div>
              <div>
                <img src={fourthStep} alt="parent" />
              </div>
              <span className="message-title">Go To Parent’s Dashboard</span>
              <p className="message-text">
                See an overview of all your scholars and keep track of their
                digital locations from one screen. Manage your Aneta account.
              </p>
            </div>
            <img className={styles.lockTp} src={Lock} alt="" />
          </button>
        </div>
      ) : (
        <div className={styles.selectBlock}>
          <button
            type="button"
            onClick={() =>
              history.push({
                pathname: '/onboarding/profile',
                state: { mode: 'create' },
              })
            }
            className={`${styles.selectBlockItemTop} ${styles.trapezoid}`}
          >
            <div>
              <div>
                <img src={firstStep} alt="teacher" />
              </div>
              <span className="message-title">Create An Anetapack Profile</span>
              <p className="message-text">
                An Anetapack, like a helpful backpack, is a particular
                collection of digital content. The profile describes the grade
                level, learning skills and interests it covers.
              </p>
            </div>
          </button>
          <button
            type="button"
            className={`${styles.selectBlockItemBottom} ${styles.trapezoid}`}
            onClick={() => history.push('/onboarding/timetable')}
          >
            <div>
              <div>
                <img src={thirdStep} alt="teacher" />
              </div>
              <span className="message-title">
                Add Timetable and Web Addresses
              </span>
              <p className="message-text">
                Add content to the Anetapack. You can include a schedule and web
                addresses that point to the digital content.
              </p>
            </div>
          </button>
          <button
            type="button"
            className={`${styles.selectBlockItemTop} ${styles.trapezoid}`}
            onClick={() => history.push('/dashboard/scholars')}
          >
            <div>
              <div>
                <img src={fourthStep} alt="teacher" />
              </div>
              <span className="message-title">Go To Dashboard</span>
              <p className="message-text">
                Manage all the Anetapacks you have created and view Anetapacks
                from other curators.
              </p>
            </div>
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={() => {
          if (user?.type === 'teacher') {
            history.push({
              pathname: '/onboarding/profile',
              state: { mode: 'create' },
            });
          } else {
            history.push('/map');
          }
        }}
        className={buttons.buttomPrimary}
      >
        {user?.type === 'parent' ? 'Return to map' : 'Let’s Go'}
      </button>
    </div>
  );
};

export default connect(mapStateToProps)(StepsAdminCorner);
